import { useEffect, useState, useRef } from 'react'
import { Plus, Trash2, X, Loader2, RefreshCw, Images, Image, ArrowLeft, Upload } from 'lucide-react'
import { galleryApi } from '../api/services.js'
import toast from 'react-hot-toast'

export default function GalleryPage() {
  const [albums, setAlbums] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedAlbum, setSelectedAlbum] = useState(null)
  const [media, setMedia] = useState([])
  const [loadingMedia, setLoadingMedia] = useState(false)
  const [addAlbumModal, setAddAlbumModal] = useState(false)
  const [albumForm, setAlbumForm] = useState({ name: '', description: '' })
  const [savingAlbum, setSavingAlbum] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deleteAlbumId, setDeleteAlbumId] = useState(null)
  const fileInputRef = useRef(null)

  const fetchAlbums = async () => {
    try {
      setLoading(true)
      const data = await galleryApi.getAlbums()
      setAlbums(Array.isArray(data) ? data : data?.albums ?? [])
    } catch { toast.error('Failed to load albums'); setAlbums([]) }
    finally { setLoading(false) }
  }

  useEffect(() => { fetchAlbums() }, [])

  const openAlbum = async (album) => {
    setSelectedAlbum(album)
    try {
      setLoadingMedia(true)
      const data = await galleryApi.getMedia(album._id || album.id)
      setMedia(Array.isArray(data) ? data : data?.media ?? [])
    } catch { toast.error('Failed to load media'); setMedia([]) }
    finally { setLoadingMedia(false) }
  }

  const handleCreateAlbum = async () => {
    if (!albumForm.name.trim()) { toast.error('Album name required'); return }
    try {
      setSavingAlbum(true)
      const created = await galleryApi.createAlbum(albumForm)
      setAlbums((prev) => [...prev, created])
      toast.success('Album created')
      setAddAlbumModal(false)
      setAlbumForm({ name: '', description: '' })
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to create album') }
    finally { setSavingAlbum(false) }
  }

  const handleDeleteAlbum = async () => {
    try {
      await galleryApi.deleteAlbum(deleteAlbumId)
      setAlbums((prev) => prev.filter((a) => (a._id || a.id) !== deleteAlbumId))
      toast.success('Album deleted')
      if (selectedAlbum && (selectedAlbum._id || selectedAlbum.id) === deleteAlbumId) setSelectedAlbum(null)
      setDeleteAlbumId(null)
    } catch { toast.error('Failed to delete album') }
  }

  const handleUpload = async (e) => {
    const files = e.target.files
    if (!files?.length || !selectedAlbum) return
    try {
      setUploading(true)
      const aid = selectedAlbum._id || selectedAlbum.id
      for (const file of files) {
        const form = new FormData()
        form.append('media', file)
        const uploaded = await galleryApi.uploadMedia(aid, form)
        const item = Array.isArray(uploaded) ? uploaded[0] : uploaded
        if (item) setMedia((prev) => [...prev, item])
      }
      toast.success(`${files.length} file${files.length > 1 ? 's' : ''} uploaded`)
    } catch (err) { toast.error(err.response?.data?.message || 'Upload failed') }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = '' }
  }

  const handleDeleteMedia = async (mediaId) => {
    try {
      await galleryApi.deleteMedia(mediaId)
      setMedia((prev) => prev.filter((m) => (m._id || m.id) !== mediaId))
      toast.success('Deleted')
    } catch { toast.error('Failed to delete') }
  }

  // Album list view
  if (!selectedAlbum) return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Gallery</h2>
          <p className="text-sm text-gray-500 mt-0.5">{loading ? '...' : `${albums.length} albums`}</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button onClick={fetchAlbums} className="btn-secondary p-2"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={() => setAddAlbumModal(true)} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> New Album</button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {loading ? [...Array(6)].map((_, i) => (
          <div key={i} className="card animate-pulse">
            <div className="h-36 bg-gray-100 rounded-t-2xl" />
            <div className="p-3 space-y-2"><div className="h-4 bg-gray-100 rounded w-2/3" /><div className="h-3 bg-gray-100 rounded w-1/2" /></div>
          </div>
        )) : albums.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-400">
            <Images className="w-10 h-10 mx-auto mb-3 text-gray-200" />
            <p className="text-sm">No albums yet. Create one to get started.</p>
          </div>
        ) : albums.map((a) => (
          <div key={a._id || a.id} className="card overflow-hidden cursor-pointer group" onClick={() => openAlbum(a)}>
            <div className="h-36 bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center relative overflow-hidden">
              {a.coverImage || a.thumbnail ? (
                <img src={a.coverImage || a.thumbnail} alt={a.name} className="w-full h-full object-cover" />
              ) : (
                <Images className="w-12 h-12 text-purple-200" />
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </div>
            <div className="p-3 flex items-start justify-between">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 text-sm truncate">{a.name}</p>
                <p className="text-xs text-gray-400">{a.mediaCount ?? a.count ?? 0} photos</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setDeleteAlbumId(a._id || a.id) }}
                className="p-1 rounded text-gray-300 hover:text-red-400 transition-colors flex-shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Album Modal */}
      {addAlbumModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">New Album</h3>
              <button onClick={() => setAddAlbumModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Album Name *</label>
                <input className="input-field w-full" value={albumForm.name} onChange={(e) => setAlbumForm({ ...albumForm, name: e.target.value })} placeholder="e.g. Annual Day 2025" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                <textarea className="input-field w-full h-16 resize-none" value={albumForm.description} onChange={(e) => setAlbumForm({ ...albumForm, description: e.target.value })} placeholder="Optional description" />
              </div>
            </div>
            <div className="px-6 pb-5 flex gap-3 justify-end">
              <button onClick={() => setAddAlbumModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleCreateAlbum} disabled={savingAlbum} className="btn-primary flex items-center gap-2">
                {savingAlbum && <Loader2 className="w-4 h-4 animate-spin" />} Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete album confirm */}
      {deleteAlbumId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4"><Trash2 className="w-5 h-5 text-red-500" /></div>
            <h3 className="font-bold text-gray-900 mb-1">Delete Album?</h3>
            <p className="text-sm text-gray-500 mb-5">All photos in this album will be deleted.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteAlbumId(null)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleDeleteAlbum} className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  // Album media view
  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => setSelectedAlbum(null)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{selectedAlbum.name}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{media.length} photos</p>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={handleUpload} />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn-primary flex items-center gap-2"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload
          </button>
        </div>
      </div>

      {loadingMedia ? (
        <div className="flex items-center justify-center h-48"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
      ) : media.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Image className="w-12 h-12 mx-auto mb-3 text-gray-200" />
          <p className="text-sm">No photos yet.</p>
          <button onClick={() => fileInputRef.current?.click()} className="mt-3 btn-primary text-sm">Upload Photos</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {media.map((m) => (
            <div key={m._id || m.id} className="relative group rounded-xl overflow-hidden bg-gray-100 aspect-square">
              {m.url || m.mediaUrl ? (
                <img src={m.url || m.mediaUrl} alt={m.title || ''} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-8 h-8 text-gray-300" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <button
                  onClick={() => handleDeleteMedia(m._id || m.id)}
                  className="p-2 bg-red-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
